import { Gate as CoralGate, GateBadge as Badge, GateError, Icon, usePairingCode } from "@cliqsuite/ui";
import type { ReactNode } from "react";

const Gate = ({ side, children }: { side: [string, string]; children: ReactNode }) => (
  <CoralGate
    app="KDS"
    side={
      <>
        <b>{side[0]}</b>
        <small>{side[1]}</small>
      </>
    }
  >
    {children}
  </CoralGate>
);

export function PairScreen({ error, pairing, onPair }: { error: string; pairing: boolean; onPair: (code: string) => void }) {
  const { code, boxes } = usePairingCode({ error: !!error, onSubmit: onPair });
  return (
    <Gate side={["Kitchen display", "Get a code in the Business app, under Devices"]}>
      <div>
        <h1>Pair this display</h1>
        <p>Type the Kitchen Display code from the Business app, under Devices.</p>
      </div>
      {boxes}
      {error && <GateError>{error}</GateError>}
      <button className="gate-primary" disabled={pairing || code.length < 6} onClick={() => onPair(code)}>
        {pairing ? "Pairing…" : "Pair display"}
        <Icon name="arrow_forward" />
      </button>
    </Gate>
  );
}

export function NotEnabledScreen({ storeName, onUnpair }: { storeName: string; onUnpair: () => void }) {
  return (
    <Gate side={["This screen", storeName]}>
      <Badge icon="soup_kitchen" bg="var(--amber-soft)" fg="var(--amber)" />
      <div>
        <h1>Kitchen isn&rsquo;t turned on</h1>
        <p>{storeName} doesn&rsquo;t have the Restaurant module. The owner turns it on in the Business app, under Modules.</p>
      </div>
      <button className="gate-link" onClick={onUnpair}>
        <Icon name="link_off" size={18} />
        Unpair this display
      </button>
    </Gate>
  );
}

export function UnpairedScreen({ storeName, onPairAgain }: { storeName: string; onPairAgain: () => void }) {
  return (
    <Gate side={["This screen was", "Main kitchen"]}>
      <Badge icon="link_off" bg="var(--pink)" fg="var(--coral)" />
      <div>
        <h1>This display was unpaired</h1>
        <p>It was removed from {storeName} in the Business app. New tickets won&rsquo;t show here until it&rsquo;s paired again.</p>
      </div>
      <div className="gate-facts">
        <div>
          <Icon name="soup_kitchen" size={18} color="var(--text2)" />
          Open tickets are still on the other kitchen screens and the POS
        </div>
      </div>
      <button className="gate-primary" onClick={onPairAgain}>
        <Icon name="add_link" />
        Pair this display again
      </button>
    </Gate>
  );
}

export function PlanEndedScreen({ storeName }: { storeName: string }) {
  return (
    <Gate side={["Plan ended", storeName]}>
      <Badge icon="event_busy" bg="var(--amber-soft)" fg="var(--amber)" />
      <div>
        <h1>This business&rsquo;s plan has ended</h1>
        <p>New tickets stop coming in until the owner renews in the Business app, under Billing &amp; plan. It unlocks on its own once paid.</p>
      </div>
      <div className="gate-facts">
        <div>
          <Icon name="receipt_long" size={18} color="var(--text2)" />
          Tickets already sent stay on the POS Kitchen tab
        </div>
      </div>
    </Gate>
  );
}
